<template>
  <div>
    <div
      ref="blockRef"
      :class="[
        'relative pb-8 lg:min-h-[722px] lg:pb-16 lg:pt-12',
        useDarkMode ? 'bg-black-dark' : 'bg-blue-lighter',
      ]"
    >
      <Image
        v-if="data?.image?.url"
        :url="data?.image?.url"
        :alt="data.image.alt"
        class="right-0 aspect-[378/310] w-full lg:!absolute lg:bottom-5 lg:top-5 lg:aspect-auto lg:rounded-bl-2xl lg:rounded-tl-2xl xl:aspect-[832/684] xl:h-[684px] xl:max-w-[832px]"
        :style="imageStyle"
        width="832"
        height="684"
        sizes="xs:100vw sm:100vw md:600px lg:800px xl:832px"
        fit="cover"
      />
      <Container class="mt-10 flex lg:mt-0">
        <div class="basis-full lg:basis-1/2">
          <SubTitle class="text-blue">{{ data?.subtitle }}</SubTitle>
          <Title :custom-style="useDarkMode ? 'text-white' : ''">{{ data?.title }}</Title>

          <div
            class="lg:scrollbar-thin lg:scrollbar-track-white lg:scrollbar-thumb-blue lg:scrollbar-track-rounded-full lg:scrollbar-thumb-rounded-full -mb-4 -ml-4 mt-6 overflow-hidden pb-4 pl-4 pt-2 transition-all duration-700 ease-in-out lg:mt-14 lg:!max-h-[476px] lg:overflow-y-auto"
            :style="heightBlock"
          >
            <ul class="space-y-2">
              <li v-for="(link, index) in data?.links" :key="index">
                <a
                  :href="normalizeURL(`#${slugify(link.blockTitle || link.id)}`)"
                  class="cursor-pointer font-sans text-base font-bold uppercase text-blue drop-shadow-[0_1px_5px_rgba(0,155,181,0.4)] hover:text-blue-dark hover:drop-shadow-none lg:text-xl"
                >
                  {{ link.title }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </Container>
      <div
        v-if="!isShow"
        class="absolute bottom-0 flex h-32 w-full items-end justify-center bg-gradient-to-t from-blue-lighter via-blue-lighter to-transparent pb-8 lg:hidden"
      >
        <button
          class="flex h-9 w-9 items-center justify-center rounded-lg border-[3px] border-blue"
          aria-label="open"
          @click="isShow = true"
        >
          <Plus />
        </button>
      </div>
    </div>
    <ButtonUp :block-to="blockRef" />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed, inject } from 'vue'
import { useWindowSize } from '@vueuse/core'
import { normalizeURL } from 'ufo'
import slugify from '@sindresorhus/slugify'

import type { Ref, CSSProperties } from 'vue'

import SubTitle from '../atoms/SubTitle.vue'
import Title from '../atoms/Title.vue'
import Container from '../atoms/Container.vue'
import Image from '../atoms/Image.vue'
import ButtonUp from '../atoms/ButtonUp.vue'
import type { IImageData } from '../../types'
import Plus from '../atoms/Icons/Plus.vue'

type HeightBlockStyle = CSSProperties & {
  maxHeight: string
}

export interface IOnThisPageBlock {
  id?: string
  image?: IImageData
  title?: string
  subtitle?: string
  links?: {
    id: string
    title: string
    blockTitle: string
    anchorTitle: string
  }[]
}

interface Props {
  data: IOnThisPageBlock
}

defineProps<Props>()

const settings = inject<string[]>('settings', [])
const useDarkMode = computed(() => settings.includes('dark_theme') ?? false)

const imageWidth = ref('100%')
const isShow = ref(false)

const blockRef: Ref<HTMLDivElement | undefined> = ref()

const { width: widthWindow } = useWindowSize()

const imageStyle = computed(() => {
  return widthWindow.value > 1024
    ? {
        width: typeof imageWidth.value === 'string' ? imageWidth.value : imageWidth.value + 'px',
      }
    : { width: '100%' }
})

const heightBlock = computed<HeightBlockStyle>(() => {
  return isShow.value ? { maxHeight: '5000px' } : { maxHeight: '476px' }
})

onMounted(() => {
  setTimeout(() => {
    imageWidth.value = widthWindow.value / 2 - 38 + 'px'
  }, 100)
})

watch(widthWindow, () => {
  imageWidth.value = '0'

  setTimeout(() => {
    imageWidth.value = widthWindow.value / 2 - 38 + 'px'
  }, 100)
})
</script>

<style>
html {
  scroll-behavior: smooth;
}
</style>
